import { Button, Flex, Form } from "antd"
import { Modal } from "ui/Modal"
import { useTranslation } from "react-i18next"
import { spacings } from "themes/variables"
import PropTypes from "prop-types"
import Input from "components/form/Input"
import LabeledField from "../../Tabs/ProfileInfo/LabeledField"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"
import { useSelector } from "react-redux"

const UpdateEmail = ({
  open,
  onClose,
  setOpen,
  hasSentCode,
  cooldown,
  isSending,
  sendCodeLabel,
  handleSendCode,
  handleConfirmCode,
  handleUpdateReset,
  existingEmail
}) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const [form] = Form.useForm()
  const isSendOtpLoading = useSelector((state) => state.user?.sendOtpToVerifyEmail?.loading || false)
  const isVerifyOtpLoading = useSelector((state) => state.user?.verifyOtpForEmailUpdation?.loading || false)

  const handleCancel = () => {
    form.resetFields()
    handleUpdateReset()
  }

  const handleConfirmClick = async () => {
    try {
      const { input_code } = (await form.validateFields(["input_code"])) || {}
      if (!input_code) return
      const emailValue = form.getFieldValue("email")
      await handleConfirmCode(emailValue, input_code)
    } catch (error) {
      console.error(error)
    }
  }

  const handleSendOTP = async () => {
    try {
      const { email } = (await form.validateFields(["email"])) || {}
      await handleSendCode(email)
      form.resetFields(["input_code"])
    } catch (error) {
      console.error(error)
    }
  }

  const modalContent = () => {
    return (
      <Form form={form}>
        <Flex vertical className="mb-m" gap={spacings.S}>
          <LabeledField
            labelKey="login_security.enter_new_email"
            name="email"
            validateTrigger={["onChange", "onBlur"]}
            rules={[
              { required: true, message: t("client.profile.tabs.profile.email_is_required") },
              { type: "email", message: t("form_validation.invalid_email") },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve()
                  if (value?.trim().toLowerCase() === existingEmail?.trim().toLowerCase()) {
                    return Promise.reject(new Error(t("form_validation.email_same_as_existing")))
                  }
                  return Promise.resolve()
                }
              }
            ]}
          >
            <Input disabled={hasSentCode} />
          </LabeledField>
          <Flex gap={spacings.S}>
            <Form.Item shouldUpdate>
              {() => {
                const emailValue = form.getFieldValue("email")
                const hasErrors = form.getFieldError ? form.getFieldError("email")?.length > 0 : false
                const isValid = !!emailValue && !hasErrors
                const isDisabled = isSending || cooldown > 0 || !isValid || isSendOtpLoading
                return (
                  <Button type="primary" htmlType="button" onClick={handleSendOTP} disabled={isDisabled}>
                    {sendCodeLabel}
                  </Button>
                )
              }}
            </Form.Item>
            {hasSentCode && (
              <Button color="primary" variant="outlined" onClick={handleCancel} disabled={isVerifyOtpLoading}>
                {t("global.cancel")}
              </Button>
            )}
          </Flex>

          {hasSentCode && (
            <Flex vertical>
              <LabeledField
                name="input_code"
                labelKey="client.profile.tabs.profile.input_code"
                rules={[
                  { required: true, message: t("client.profile.tabs.profile.code_required") },
                  { max: 5, message: t("form_validation.five_digit_code") }
                ]}
              >
                <Input type="number" />
              </LabeledField>
              <Button
                type="primary"
                onClick={handleConfirmClick}
                className="w-fit"
                disabled={isVerifyOtpLoading}
                loading={isVerifyOtpLoading}
              >
                {t("global.confirm")}
              </Button>
            </Flex>
          )}
        </Flex>
      </Form>
    )
  }

  return (
    <>
      {isMobile ? (
        <CustomDrawer open={open} setOpen={setOpen} onCancel={onClose} onClose={onClose} title={t("login_security.update_email")}>
          {modalContent()}
        </CustomDrawer>
      ) : (
        <Modal
          title={t("login_security.update_email")}
          open={open}
          onCancel={isVerifyOtpLoading ? undefined : onClose}
          width={600}
          centered
        >
          {modalContent()}
        </Modal>
      )}
    </>
  )
}

UpdateEmail.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  hasSentCode: PropTypes.bool.isRequired,
  cooldown: PropTypes.number.isRequired,
  isSending: PropTypes.bool.isRequired,
  sendCodeLabel: PropTypes.string.isRequired,
  handleSendCode: PropTypes.func.isRequired,
  handleConfirmCode: PropTypes.func.isRequired,
  handleUpdateReset: PropTypes.func.isRequired,
  existingEmail: PropTypes.string.isRequired
}

export default UpdateEmail
